import { makeStyles } from "@material-ui/styles";
import { ArrowBack } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import {
  DeploymentLabel,
  dermaUrlInRegion,
  isKnownRegion,
  namesThatDistinguish,
} from "lib/region";
import { useNavigate, useSearchParams } from "react-router-dom";
import Badge from "render/components/Badge";
import { useConfig } from "render/context/ConfigContext";

const useStyles = makeStyles({
  WrongRegionPage: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    "& .header": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "8px",
      margin: "6px",
      "& .name": {
        fontSize: "18px",
        fontWeight: 500,
      },
      "& .externalId": {
        fontSize: "12px",
        opacity: 0.7,
      },
    },
    "& .notice": {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
      borderTop: "1px solid rgba(255, 255, 255, 0.2)",
      margin: "6px",
      padding: "24px 12px",
      "& h2": {
        margin: 0,
        fontSize: "20px",
      },
      "& p": {
        margin: 0,
        fontSize: "14px",
        lineHeight: "20px",
      },
      "& .action": {
        display: "inline-block",
        padding: "10px 16px",
        borderRadius: "6px",
        backgroundColor: "#3f51b5",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 500,
        textDecoration: "none",
      },
    },
  },
});

interface WrongRegionPageProps {
  name: string;
  externalId: string;
  memberRegion: DeploymentLabel;
  servingRegion: DeploymentLabel;
}

/**
 * Stands in for a member's record when their data is held in another region.
 *
 * Three facts survive: name, identifier and data location, which is enough to confirm the
 * right person before following the link. Nothing else of the record renders, so no
 * individual action needs disabling and no write can reach the wrong region from here.
 */
export default function WrongRegionPage({
  name,
  externalId,
  memberRegion,
  servingRegion,
}: WrongRegionPageProps) {
  const styles = useStyles();
  const config = useConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { first: memberRegionName, second: servingRegionName } =
    namesThatDistinguish(memberRegion, servingRegion);

  // The member's lesion list, not the page being left. The add-image route carries an
  // annotation id in its query string, and that annotation was written in whichever region
  // created it, so replaying the current URL can land the clinician on a lesion the other
  // region cannot find. The lesion list enumerates what is actually there. The link's
  // issuedAt goes along, since the other region opens no member without one.
  const query = new URLSearchParams({
    issuedAt: searchParams.get("issuedAt") ?? "",
  });
  const memberRegionUrl = dermaUrlInRegion(
    memberRegion,
    config.stageName,
    `/lesions/${encodeURIComponent(externalId)}?${query}`
  );

  return (
    <div className={styles.WrongRegionPage}>
      <div className="header">
        <IconButton
          color="primary"
          size="large"
          onClick={() => {
            navigate("/");
          }}
        >
          <Tooltip title="Back to start page">
            <ArrowBack fontSize="inherit" />
          </Tooltip>
        </IconButton>
        <span className="name">{name}</span>
        <span className="externalId">{externalId}</span>
        <Badge text={`Data stored in ${memberRegionName}`} intent="warning" />
      </div>

      <div className="notice">
        <h2>This member's data is held in {memberRegionName}</h2>
        <p>
          This app is working in {servingRegionName}, so none of this member's
          record is shown here and an image added here would not reach it.
        </p>
        {memberRegionUrl ? (
          <a className="action" href={memberRegionUrl}>
            Open this member in {memberRegionName}
          </a>
        ) : isKnownRegion(memberRegion) ? (
          <p>
            This environment serves every region from one app, so there is no
            separate {memberRegionName} Derma to open from here. On a clinic
            device, open this member in the {memberRegionName} Derma.
          </p>
        ) : (
          <p>
            This app has no address for the region {memberRegionName}, so it
            cannot open this member there. Report this to support and quote the
            region name {memberRegionName}.
          </p>
        )}
      </div>
    </div>
  );
}
